import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp, formatCurrency } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, CalendarIcon, TrendingDown } from "lucide-react";
import { format, subDays, subMonths, isWithinInterval, parseISO } from "date-fns";

const DATE_PRESETS = [
  { label: "This cycle only", value: "cycle" },
  { label: "Last 7 days", value: "7days" },
  { label: "Last 30 days", value: "30days" },
  { label: "Last 3 months", value: "3months" },
  { label: "All time", value: "all" },
  { label: "Custom range", value: "custom" },
];

export default function ExpenseDetails() {
  const navigate = useNavigate();
  const { transactions, categories, selectedCycle, getCycleDateRange } = useApp();
  
  const currentCycleRange = selectedCycle ? getCycleDateRange(selectedCycle) : null;
  
  const [datePreset, setDatePreset] = useState("cycle");
  const [startDate, setStartDate] = useState(() => currentCycleRange?.start || new Date());
  const [endDate, setEndDate] = useState(() => currentCycleRange?.end || new Date());
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Initialize dates when cycle changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (currentCycleRange && datePreset === "cycle") {
      setStartDate(currentCycleRange.start);
      setEndDate(currentCycleRange.end);
    }
  }, [selectedCycle]); // Only run when selectedCycle changes

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    
    switch (preset) {
      case "cycle":
        if (currentCycleRange) {
          setStartDate(currentCycleRange.start);
          setEndDate(currentCycleRange.end);
        }
        setShowCustomRange(false);
        break;
      case "7days": setStartDate(subDays(now, 7)); setEndDate(now); setShowCustomRange(false); break;
      case "30days": setStartDate(subDays(now, 30)); setEndDate(now); setShowCustomRange(false); break;
      case "3months": setStartDate(subMonths(now, 3)); setEndDate(now); setShowCustomRange(false); break;
      case "all": setStartDate(new Date(2000, 0, 1)); setEndDate(now); setShowCustomRange(false); break;
      case "custom": setShowCustomRange(true); break;
      default: break;
    }
  };

  const filteredTransactions = useMemo(() => {
    return transactions
      .filter(t => t.type === "expense")
      .filter(t => {
        if (datePreset === "cycle") {
          return t.cycleKey === selectedCycle;
        }
        const txDate = parseISO(t.dateISO);
        return isWithinInterval(txDate, { start: startDate, end: endDate });
      })
      .sort((a, b) => new Date(b.dateISO) - new Date(a.dateISO));
  }, [transactions, selectedCycle, datePreset, startDate, endDate]);

  const totalAmount = filteredTransactions.reduce((sum, t) => sum + t.amount, 0);

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || "Unknown";
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expense-details-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-rose-100">
            <TrendingDown className="w-6 h-6 text-rose-600" />
          </div>
          <div>
            <h2 className="font-heading text-3xl text-stone-900">Expense Details</h2>
            <p className="text-stone-500">{currentCycleRange?.label}</p>
          </div>
        </div>
      </div>

      {/* Date Range Selector */}
      <Card className="p-4 bg-white rounded-2xl border border-stone-200/60">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-stone-500" />
            <span className="text-sm font-medium text-stone-600">Date Range:</span>
          </div>
          
          <Select value={datePreset} onValueChange={handlePresetChange}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Select range" /></SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {showCustomRange && (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[140px]">{format(startDate, "dd MMM yyyy")}</Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={(date) => date && setStartDate(date)} initialFocus />
                </PopoverContent>
              </Popover>
              <span className="text-stone-400">to</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[140px]">{format(endDate, "dd MMM yyyy")}</Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={(date) => date && setEndDate(date)} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-6 rounded-2xl bg-rose-50 border-rose-200">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Total Expenses</p>
          <p className="font-body font-bold text-3xl tracking-tighter text-rose-700">{formatCurrency(totalAmount)}</p>
        </Card>
        <Card className="p-6 bg-stone-50 rounded-2xl border border-stone-200">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Transactions</p>
          <p className="font-body font-bold text-3xl tracking-tighter text-stone-900">{filteredTransactions.length}</p>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card className="bg-white rounded-2xl border border-stone-200/60 overflow-hidden">
        {filteredTransactions.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Category</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((tx) => (
                  <TableRow key={tx.id} className="hover:bg-stone-50">
                    <TableCell className="font-mono text-sm">
                      {new Date(tx.dateISO).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </TableCell>
                    <TableCell>
                      <span 
                        className="font-medium cursor-pointer hover:underline text-rose-700"
                        onClick={() => navigate(`/categories/${tx.categoryId}`)}
                      >
                        {getCategoryName(tx.categoryId)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-rose-700">
                      -{formatCurrency(tx.amount)}
                    </TableCell>
                    <TableCell className="text-stone-500 max-w-[300px] truncate">{tx.notes || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-stone-400">
            <p className="font-medium">No expense transactions found</p>
            <p className="text-sm">Try adjusting the date range</p>
          </div>
        )}
      </Card>
    </div>
  );
}
